import type {createBusinessRuntime} from '../../../packages/runtime/business-runtime.ts';
import type {
  P02ActionViewModel,
  P02AlertViewModel,
  P02ReviewDetailViewModel,
  P02ReviewListItemViewModel,
  SmtRuntimePort,
  SmtUiIntent,
  SmtViewModel,
} from './contracts.ts';
import type {SmtRuntimeAdapterConfig} from './smt-runtime-adapter.ts';

type BusinessRuntime = ReturnType<typeof createBusinessRuntime>;

type PaymentProofRuntime = Pick<BusinessRuntime,
  | 'previewChannelSubmission'
  | 'channelPaymentProof'
  | 'approveChannelPaymentProof'
  | 'requestChannelPaymentProofResubmission'
>;

const proofActionIds = new Set([
  'payment-proof-approve',
  'payment-proof-request-resubmission',
]);

const isAcceptAction = (action: P02ActionViewModel): boolean =>
  action.actionId === 'accept' || action.actionId === 'accept-authoritative';

const paymentStatus = (
  source: string,
  paymentMode: string | undefined,
  proofStatus: string | undefined,
): {readonly label: string | null; readonly ready: boolean; readonly highPriority: boolean} => {
  if (source === 'customer' && !paymentMode) {
    return {label: '付款方式未設定', ready: false, highPriority: true};
  }
  if (paymentMode !== 'electronic-proof') {
    return {label: null, ready: true, highPriority: false};
  }
  if (proofStatus === 'approved') {
    return {label: '付款證明已核對', ready: true, highPriority: false};
  }
  if (proofStatus === 'resubmission_required') {
    return {label: '等待重新提交付款證明', ready: false, highPriority: true};
  }
  return {label: '待核對付款證明', ready: false, highPriority: true};
};

const decorateDetail = (
  runtime: PaymentProofRuntime,
  detail: P02ReviewDetailViewModel,
): {readonly detail: P02ReviewDetailViewModel; readonly highPriority: boolean; readonly issueLabel?: string; readonly issueSummary?: string} => {
  const preview = runtime.previewChannelSubmission(detail.reviewId);
  const envelope = preview.envelope;
  const paymentMode = envelope.payload.paymentMode;
  const proof = paymentMode === 'electronic-proof'
    ? runtime.channelPaymentProof(envelope.submissionId)
    : null;
  const state = paymentStatus(envelope.source, paymentMode, proof?.status);

  if (!state.label) return {detail, highPriority: false};

  const alerts: P02AlertViewModel[] = detail.alerts.filter((alert) =>
    alert.alertId !== 'payment-mode-required' && alert.alertId !== 'payment-proof');
  const actions: P02ActionViewModel[] = detail.actions
    .filter((action) => !proofActionIds.has(action.actionId))
    .map((action) => isAcceptAction(action) ? {...action, enabled: action.enabled && state.ready} : action);

  let issueLabel = state.label;
  let issueSummary = state.label;

  if (envelope.source === 'customer' && !paymentMode) {
    alerts.unshift({
      alertId: 'payment-mode-required',
      tone: 'red',
      title: '客戶付款方式未設定',
      body: '禁止確認落單。客戶端必須先提交到店付款或電子付款證明模式。',
    });
    issueSummary = '客戶端未提交付款方式，Runtime 會拒絕接受訂單。';
  } else if (paymentMode === 'electronic-proof') {
    const evidenceRef = proof?.currentEvidenceRef ?? envelope.payload.paymentEvidenceRef ?? '證明未建立';
    const reason = proof?.reviewReason ? `；原因：${proof.reviewReason}` : '';
    alerts.unshift({
      alertId: 'payment-proof',
      tone: proof?.status === 'approved' ? 'green' : proof?.status === 'resubmission_required' ? 'orange' : 'blue',
      title: state.label,
      body: `證明：${evidenceRef}${reason}`,
    });
    issueSummary = proof?.status === 'approved'
      ? `電子付款證明已由店員核對：${evidenceRef}`
      : proof?.status === 'resubmission_required'
        ? `已要求客戶重新提交付款證明：${evidenceRef}${reason}`
        : `等待店員核對電子付款證明：${evidenceRef}`;

    if (proof?.status === 'pending') {
      actions.unshift(
        {actionId: 'payment-proof-approve', label: '證明核對通過', kind: 'secondary', enabled: true},
        {actionId: 'payment-proof-request-resubmission', label: '要求重新提交證明', kind: 'secondary', enabled: true},
      );
    }
  }

  return {
    detail: {...detail, statusLabel: state.label, alerts, actions},
    highPriority: state.highPriority,
    issueLabel,
    issueSummary,
  };
};

const decorateSnapshot = (runtime: PaymentProofRuntime, source: SmtViewModel): SmtViewModel => {
  const detailsByReviewId: Record<string, P02ReviewDetailViewModel> = {};
  const reviewOverrides = new Map<string, {readonly highPriority: boolean; readonly issueLabel?: string; readonly issueSummary?: string}>();

  for (const [reviewId, detail] of Object.entries(source.p02.detailsByReviewId)) {
    const decorated = decorateDetail(runtime, detail);
    detailsByReviewId[reviewId] = decorated.detail;
    reviewOverrides.set(reviewId, decorated);
  }

  const reviews: P02ReviewListItemViewModel[] = source.p02.reviews.map((review) => {
    const override = reviewOverrides.get(review.reviewId);
    if (!override) return review;
    const issueLabel = override.issueLabel ?? review.issueLabel;
    const issueSummary = override.issueSummary ?? review.issueSummary;
    return {
      ...review,
      priority: override.highPriority ? 'high' : review.priority,
      issueLabel,
      issueSummary,
      searchText: [review.searchText, issueLabel, issueSummary].join(' '),
      tone: override.highPriority ? 'orange' : review.tone,
    };
  });

  const highPriorityLabel = String(reviews.filter((review) => review.priority === 'high').length);
  return {
    ...source,
    p02: {
      ...source.p02,
      reviews,
      detailsByReviewId,
      highPriorityLabel,
    },
  };
};

/**
 * Adds payment-proof review presentation/actions without becoming a second
 * Review, Order or Payment authority. The base port remains the operational
 * adapter; all proof mutations delegate to the canonical Business Runtime.
 */
export function decorateSmtPaymentProofReviewPort(
  base: SmtRuntimePort,
  config: Pick<SmtRuntimeAdapterConfig, 'runtime' | 'operatorId' | 'now' | 'nextId'>,
): SmtRuntimePort {
  const runtime = config.runtime as PaymentProofRuntime;
  const listeners = new Set<() => void>();
  let baseSnapshot = base.getSnapshot();
  let snapshot = decorateSnapshot(runtime, baseSnapshot);

  const refresh = (): void => {
    const nextBase = base.getSnapshot();
    baseSnapshot = nextBase;
    snapshot = decorateSnapshot(runtime, nextBase);
    for (const listener of listeners) listener();
  };

  base.subscribe(() => refresh());

  const dispatchProofAction = (intent: Extract<SmtUiIntent, {readonly type: 'p02.review-action.requested'}>): boolean => {
    if (!proofActionIds.has(intent.actionId)) return false;
    const proof = runtime.channelPaymentProof(intent.reviewId);
    if (!proof) throw new Error('SMT_PAYMENT_PROOF_REQUIRED');
    const at = config.now();
    if (intent.actionId === 'payment-proof-approve') {
      runtime.approveChannelPaymentProof(intent.reviewId, {
        expectedVersion: proof.version,
        idempotencyKey: config.nextId('idempotency'),
        actorId: config.operatorId,
        at,
      });
    } else {
      runtime.requestChannelPaymentProofResubmission(intent.reviewId, {
        expectedVersion: proof.version,
        idempotencyKey: config.nextId('idempotency'),
        actorId: config.operatorId,
        at,
        reason: 'operator_requested_clearer_payment_proof',
      });
    }
    refresh();
    return true;
  };

  return {
    ...base,
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispatch(intent) {
      if (intent.type === 'p02.review-action.requested' && dispatchProofAction(intent)) return;
      return base.dispatch(intent);
    },
  };
}
